import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Timeline,
  Typography
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import OnTheWayButton from "../notifications/OnTheWayButton";
import { checkPlan, createJob, deleteJob, getJob, updateJob } from "./api";
import { berlinInstant, clockToMinutes, dayOf } from "./calc";
import { ConflictList, saveWithAcknowledgement } from "./Conflicts";
import { clockOf, kindLabels, qualificationLabels } from "./labels";

const DAY = "YYYY-MM-DD";
const CLOCKS = Array.from(
  { length: 96 },
  (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`
);

/** { start, end } (ISO) from the form's day and clock fields, or null. */
const rangeOf = values => {
  const day = values.day && values.day.format(DAY);
  const endDay = (values.endDay && values.endDay.format(DAY)) || day;
  const from = clockToMinutes(values.startTime);
  const to = clockToMinutes(values.endTime);
  if (!day || from == null || to == null) return null;
  const start = berlinInstant(day, from);
  const end = berlinInstant(endDay, to);
  return end > start ? { start: start.toISOString(), end: end.toISOString() } : null;
};

/**
 * Plan a new job (from an appointment, project, maintenance visit, task, or freely) or edit a
 * planned one (`id` = its appointment id): time, people, vehicle, tools, address, notes,
 * material, requirements. Conflicts are checked while typing and need acknowledgement to save.
 */
const JobDrawer = ({ open, meta, id, preset, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const timer = useRef(null);
  const editing = Boolean(id);
  const values = Form.useWatch([], form) || {};
  const baseSource = editing ? null : preset?.source || { type: "manual" };
  // A free job becomes project work as soon as a project is chosen
  const source =
    baseSource && ["manual", "project"].includes(baseSource.type) && values.project
      ? { type: "project", id: values.project }
      : baseSource?.type === "project"
        ? { type: "manual" }
        : baseSource;
  const kinds = kindLabels(t);
  const qualifications = qualificationLabels(t);
  const can = meta?.can || {};

  useEffect(() => {
    if (!open) return;
    setConflicts([]);
    form.resetFields();
    if (editing) {
      setLoading(true);
      getJob(id)
        .then(res => {
          const data = res.data.data;
          setItem(data);
          setConflicts(data.conflicts || []);
          form.setFieldsValue({
            title: data.title,
            day: dayjs(dayOf(data.start)),
            endDay: dayOf(data.end) !== dayOf(data.start) ? dayjs(dayOf(data.end)) : null,
            startTime: clockOf(data.start),
            endTime: clockOf(data.end),
            employees: data.employees,
            vehicle: data.vehicle,
            driver: data.driver,
            tools: data.tools,
            address: data.jobAddress,
            notes: data.notes,
            materials: data.materials,
            requiredSkills: data.requiredSkills,
            requiredQualifications: data.requiredQualifications
          });
        })
        .catch(error => {
          generalErrorAlert(error);
          onClose();
        })
        .finally(() => setLoading(false));
    } else {
      setItem(null);
      const defaults = preset || {};
      const firstEmployee = (defaults.employees || [])[0];
      const usual = meta?.staff?.find(s => s.id === firstEmployee)?.defaultVehicle || null;
      form.setFieldsValue({
        title: defaults.title || "",
        day: defaults.day ? dayjs(defaults.day) : dayjs(),
        startTime: defaults.startTime || meta?.settings?.dayStart || "08:00",
        endTime:
          defaults.endTime ||
          minutesLater(defaults.startTime || meta?.settings?.dayStart || "08:00", 120),
        employees: defaults.employees || [],
        vehicle: usual,
        tools: [],
        client: defaults.client || undefined,
        address: defaults.address || "",
        notifyCustomer: !["project", "task"].includes(baseSource.type),
        project: baseSource.type === "project" ? baseSource.id : undefined,
        series: false
      });
      // Project: its name as the title
      if (baseSource.type === "project") {
        const project = meta?.projects?.find(p => p.id === baseSource.id);
        if (project && !defaults.title) form.setFieldsValue({ title: project.name });
      }
      scheduleCheck();
    }
  }, [open, id]);

  const client = meta?.clients?.find(c => c.id === values.client);
  const staffOptions = useMemo(
    () => (meta?.staff || []).map(s => ({ value: s.id, label: s.name })),
    [meta]
  );
  const driverOptions = staffOptions.filter(o => (values.employees || []).includes(o.value));

  const candidate = () => {
    const v = form.getFieldsValue(true);
    const range = rangeOf(v);
    if (!range) return null;
    return {
      appointment: editing ? id : preset?.source?.type === "appointment" ? preset.source.id : null,
      ...range,
      employees: v.employees || [],
      vehicle: v.vehicle || null,
      driver: v.driver || null,
      tools: v.tools || [],
      requiredSkills: v.requiredSkills || [],
      requiredQualifications: v.requiredQualifications || []
    };
  };

  function scheduleCheck() {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const body = candidate();
      if (!body) return setConflicts([]);
      try {
        const res = await checkPlan(body);
        setConflicts(res.data.data);
      } catch {
        // The check is a preview; saving checks again
      }
    }, 400);
  }

  useEffect(() => () => clearTimeout(timer.current), []);

  const save = async () => {
    const v = await form.validateFields();
    const range = rangeOf(v);
    const series = !editing && v.series && (v.days || []).length;
    if (!series && !range) {
      generalErrorAlert({ message: t("The end must be after the start") });
      return;
    }
    const fields = {
      employees: v.employees || [],
      vehicle: v.vehicle || null,
      driver: v.driver || null,
      tools: v.tools || [],
      address: v.address || "",
      notes: v.notes || "",
      materials: v.materials || "",
      requiredSkills: v.requiredSkills || [],
      requiredQualifications: v.requiredQualifications || []
    };
    setSaving(true);
    const res = await saveWithAcknowledgement(acknowledge =>
      editing
        ? updateJob(id, { ...fields, ...range, title: v.title, acknowledge })
        : createJob({
            ...fields,
            source,
            title: v.title,
            ...(series
              ? {
                  days: v.days.map(d => d.format(DAY)),
                  startTime: v.startTime,
                  endTime: v.endTime
                }
              : range),
            ...(source.type === "manual"
              ? { client: v.client || null, clientBranch: v.clientBranch || null }
              : {}),
            notifyCustomer: Boolean(v.notifyCustomer),
            acknowledge
          })
    );
    setSaving(false);
    if (!res) return;
    Success(t("Saved"));
    onSaved();
  };

  const remove = async () => {
    try {
      await deleteJob(id);
      Success(t("Removed"));
      onSaved();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const sourceText = () => {
    if (editing) return item ? kinds[item.kind] : "";
    if (source.type === "project") {
      return `${kinds.project}: ${meta?.projects?.find(p => p.id === source.id)?.name || ""}`;
    }
    return preset?.sourceLabel
      ? `${kinds[source.type]}: ${preset.sourceLabel}`
      : kinds[source.type];
  };

  return (
    <Drawer
      rootClassName="dispatch-page"
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : 560}
      title={editing ? t("Edit job") : t("Plan job")}
      destroyOnClose
      footer={
        <div className="dsp-drawer-footer">
          {editing && can.delete && item?.job && (
            <Popconfirm
              title={t("Remove this job from the plan?")}
              description={t("An appointment created for the job is deleted too.")}
              okText={t("Remove")}
              cancelText={t("Cancel")}
              onConfirm={remove}
            >
              <Button danger icon={<DeleteOutlined />}>
                {t("Remove")}
              </Button>
            </Popconfirm>
          )}
          <span className="dsp-grow" />
          <Button onClick={onClose}>{t("Cancel")}</Button>
          <Button
            type="primary"
            loading={saving}
            onClick={save}
            disabled={editing ? !can.update : !can.create}
          >
            {t("Save")}
          </Button>
        </div>
      }
    >
      {loading ? (
        <Skeleton active />
      ) : (
        <Form form={form} layout="vertical" onValuesChange={scheduleCheck}>
          <Space wrap className="dsp-source">
            <Tag color="processing">{sourceText()}</Tag>
            {item?.client && <Tag>{item.client.name}</Tag>}
            {item?.maintenanceVisit?.number && <Tag>{item.maintenanceVisit.number}</Tag>}
          </Space>
          <Form.Item
            name="title"
            label={t("Title")}
            rules={[
              {
                required: !editing && source.type === "manual",
                message: t("Please enter a title")
              }
            ]}
          >
            <Input maxLength={200} />
          </Form.Item>
          {!editing && ["manual", "project"].includes(baseSource.type) && (
            <Form.Item
              name="project"
              label={t("Project")}
              extra={t("Project work: the project's customer, no customer messages by default.")}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={(meta?.projects || []).map(p => ({ value: p.id, label: p.name }))}
                onChange={value => {
                  const project = meta?.projects?.find(p => p.id === value);
                  if (project && !form.getFieldValue("title")) {
                    form.setFieldsValue({ title: project.name });
                  }
                  form.setFieldsValue({ notifyCustomer: !value });
                }}
              />
            </Form.Item>
          )}
          {!editing && source.type === "manual" && (
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item name="client" label={t("Customer")}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={(meta?.clients || []).map(c => ({ value: c.id, label: c.name }))}
                    onChange={() => form.setFieldsValue({ clientBranch: null })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="clientBranch" label={t("Branch")}>
                  <Select
                    allowClear
                    disabled={!client?.branches?.length}
                    options={(client?.branches || []).map(b => ({ value: b.id, label: b.name }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {!editing && ["project", "task", "manual"].includes(source.type) && (
            <Form.Item name="series" label={t("Several days")} valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
          <Row gutter={[16, 0]}>
            {values.series && !editing ? (
              <Col xs={24}>
                <Form.Item
                  name="days"
                  label={t("Days")}
                  rules={[{ required: true, message: t("Choose the days") }]}
                >
                  <DatePicker multiple className="dsp-full" format="DD.MM.YYYY" />
                </Form.Item>
              </Col>
            ) : (
              <>
                <Col xs={24} md={12}>
                  <Form.Item name="day" label={t("Day")} rules={[{ required: true }]}>
                    <DatePicker className="dsp-full" format="DD.MM.YYYY" allowClear={false} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="endDay" label={t("Until (several days)")}>
                    <DatePicker className="dsp-full" format="DD.MM.YYYY" />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={12}>
              <Form.Item name="startTime" label={t("From")} rules={[{ required: true }]}>
                <Select showSearch options={CLOCKS.map(c => ({ value: c, label: c }))} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="endTime" label={t("To")} rules={[{ required: true }]}>
                <Select showSearch options={CLOCKS.map(c => ({ value: c, label: c }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="employees" label={t("Employees")}>
            <Select mode="multiple" optionFilterProp="label" options={staffOptions} />
          </Form.Item>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name="vehicle" label={t("Vehicle")}>
                <Select
                  allowClear
                  options={(meta?.vehicles || [])
                    .filter(v => v.active || v.id === values.vehicle)
                    .map(v => ({
                      value: v.id,
                      label: [v.name, v.plate].filter(Boolean).join(" · ")
                    }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="driver" label={t("Driver")} extra={t("Default: first employee")}>
                <Select allowClear options={driverOptions} disabled={!values.vehicle} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="tools" label={t("Tools and equipment")}>
            <Select
              mode="multiple"
              optionFilterProp="label"
              options={(meta?.tools || []).map(a => ({
                value: a.id,
                label: a.type ? `${a.title} (${a.type})` : a.title
              }))}
            />
          </Form.Item>
          <Form.Item
            name="address"
            label={t("Site address")}
            extra={t("Leave empty to use the customer's (branch) address.")}
          >
            <Input maxLength={500} placeholder={item?.jobAddress ? "" : item?.address} />
          </Form.Item>
          <Form.Item name="materials" label={t("Material to bring")}>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={5000} />
          </Form.Item>
          <Form.Item name="notes" label={t("Notes for the team")}>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={5000} />
          </Form.Item>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name="requiredSkills" label={t("Required skills")}>
                <Select
                  mode="tags"
                  options={(meta?.skills || []).map(s => ({ value: s, label: s }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="requiredQualifications" label={t("Required qualifications")}>
                <Select
                  mode="multiple"
                  options={(meta?.qualificationTypes || []).map(q => ({
                    value: q,
                    label: qualifications[q] || q
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          {!editing && source.type !== "appointment" && source.type !== "maintenance" && (
            <Form.Item
              name="notifyCustomer"
              label={t("Send the customer appointment messages")}
              valuePropName="checked"
              extra={t("Confirmation, reminders and changes, per the messaging settings.")}
            >
              <Switch />
            </Form.Item>
          )}
          {conflicts.length > 0 && (
            <Alert
              type={conflicts.some(c => c.severity === "warning") ? "warning" : "info"}
              showIcon
              message={t("Check before planning")}
              description={<ConflictList conflicts={conflicts} t={t} />}
            />
          )}
          {editing && item?.client && item?.notifyCustomer && (
            <div className="dsp-section">
              <OnTheWayButton appointmentId={item.appointment} />
            </div>
          )}
          {editing && item?.history?.length > 0 && (
            <div className="dsp-section">
              <Typography.Text strong>{t("History")}</Typography.Text>
              <Timeline
                className="dsp-history"
                items={item.history
                  .slice()
                  .reverse()
                  .map((h, i) => ({
                    key: i,
                    color: h.conflicts?.length ? "orange" : "blue",
                    children: (
                      <>
                        <div>
                          {historyLabel(h.action, t)} ·{" "}
                          {new Date(h.at).toLocaleString(undefined, {
                            dateStyle: "short",
                            timeStyle: "short"
                          })}
                        </div>
                        {h.conflicts?.length > 0 && (
                          <ConflictList conflicts={h.conflicts} t={t} compact />
                        )}
                      </>
                    )
                  }))}
              />
            </div>
          )}
        </Form>
      )}
    </Drawer>
  );
};

const minutesLater = (clock, minutes) => {
  const m = Math.min((clockToMinutes(clock) ?? 480) + minutes, 23 * 60 + 45);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

const historyLabel = (action, t) =>
  ({
    create: t("Planned"),
    move: t("Moved"),
    assign: t("Reassigned"),
    update: t("Changed"),
    acknowledge: t("Warnings acknowledged")
  })[action] || action;

export default JobDrawer;
